import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type {
  DeliveryRouteRow,
  DriverRow,
  VehicleRow
} from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';
import { sumWidth, TipText } from '@hq/ui-kit';
import { hq } from '../session';

const PAGE_SIZE = 20;

const STATUS_META: Record<string, { text: string; color: string }> = {
  ENABLED: { text: '启用', color: 'green' },
  DISABLED: { text: '停用', color: 'default' }
};

const VEHICLE_TYPE_META: Record<string, string> = {
  VAN: '厢式货车',
  TRUCK: '货车',
  COLD_CHAIN: '冷链车',
  CART: '院内推车'
};

const OWNERSHIP_META: Record<string, string> = {
  SELF: '自有',
  THIRD_PARTY: '第三方'
};

const CARRIER_META: Record<string, string> = {
  SELF: '自有配送',
  THIRD_PARTY: '第三方物流',
  SUPPLIER: '供应商自送'
};

function StatusTag({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? { text: status || '-', color: 'default' };
  return <Tag color={meta.color}>{meta.text}</Tag>;
}

function StatusFilter({ value, onChange }: { value?: string; onChange: (value?: string) => void }) {
  return (
    <Select
      allowClear
      placeholder="按状态筛选"
      style={{ width: 140 }}
      value={value}
      onChange={(next) => onChange(next)}
      options={Object.entries(STATUS_META).map(([key, meta]) => ({ value: key, label: meta.text }))}
    />
  );
}

/** 配送资源主数据（运营端）：车辆 / 司机 / 线路三个页签。 */
export default function DeliveryResourceOps() {
  return (
    <Card className="page-card" title="配送资源">
      <Typography.Paragraph type="secondary">
        车辆以车牌号唯一；司机与线路编码由系统生成（`DRV001` / `RT001`），建档后不可修改。线路引用的司机/车辆须属本机构。
      </Typography.Paragraph>
      <Tabs
        items={[
          { key: 'vehicle', label: '车辆', children: <VehiclePanel /> },
          { key: 'driver', label: '司机', children: <DriverPanel /> },
          { key: 'route', label: '线路', children: <RoutePanel /> }
        ]}
      />
    </Card>
  );
}

// ---------------- 车辆 ----------------

interface VehicleFormValues {
  vehicleNo: string;
  vehicleType?: string;
  ownership?: string;
  loadCapacity?: number;
  tempZone?: string;
  remark?: string;
}

function VehiclePanel() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<VehicleRow | null>(null);
  const [form] = Form.useForm<VehicleFormValues>();

  const list = useInfiniteQuery({
    queryKey: ['ops', 'delivery-resource', 'vehicle', keyword, status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.deliveryResource.listVehicles({
        lastId: pageParam,
        size: PAGE_SIZE,
        keyword: keyword || undefined,
        status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const save = useMutation({
    mutationFn: (payload: { id?: string; values: VehicleFormValues }) =>
      hq.deliveryResource.saveVehicle({
        id: payload.id,
        vehicleNo: payload.values.vehicleNo.trim(),
        vehicleType: payload.values.vehicleType ?? 'VAN',
        ownership: payload.values.ownership ?? 'SELF',
        loadCapacity: payload.values.loadCapacity ?? 0,
        tempZone: payload.values.tempZone?.trim() || null,
        remark: payload.values.remark?.trim() || null
      }),
    onSuccess: async (result) => {
      message.success(result.created ? '车辆已建档' : '车辆已更新');
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'delivery-resource'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const toggle = useMutation({
    mutationFn: (payload: { id: string; status: 'ENABLED' | 'DISABLED' }) =>
      hq.deliveryResource.setVehicleStatus(payload.id, payload.status),
    onSuccess: async () => {
      message.success('状态已更新');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'delivery-resource'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '状态更新失败');
    }
  });

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({ vehicleType: 'VAN', ownership: 'SELF', loadCapacity: 0 });
    setOpen(true);
  };

  const openEdit = (row: VehicleRow) => {
    setEditing(row);
    form.setFieldsValue({
      vehicleNo: row.vehicleNo,
      vehicleType: row.vehicleType,
      ownership: row.ownership,
      loadCapacity: row.loadCapacity,
      tempZone: row.tempZone ?? undefined,
      remark: row.remark ?? undefined
    });
    setOpen(true);
  };

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<VehicleRow> = [
    { title: '车牌/编号', dataIndex: 'vehicleNo', width: 150, render: (value: string) => <Typography.Text code>{value}</Typography.Text> },
    {
      title: '类型 / 归属',
      key: 'type',
      width: 180,
      render: (_value: unknown, row) => (
        <Space size={4} wrap>
          <Tag>{VEHICLE_TYPE_META[row.vehicleType] ?? row.vehicleType}</Tag>
          <Tag color={row.ownership === 'SELF' ? 'blue' : 'orange'}>
            {OWNERSHIP_META[row.ownership] ?? row.ownership}
          </Tag>
        </Space>
      )
    },
    { title: '额定载重(吨)', dataIndex: 'loadCapacity', width: 120, align: 'right' },
    { title: '温区', dataIndex: 'tempZone', width: 100, render: (value: string | null) => value || '-' },
    { title: '状态', dataIndex: 'status', width: 90, render: (value: string) => <StatusTag status={value} /> },
    { title: '备注', dataIndex: 'remark', ellipsis: { showTitle: false }, render: (value: string | null) => <TipText text={value || '-'} /> },
    { title: '更新时间', dataIndex: 'updateAt', width: 170, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Space size={0}>
          <Button type="link" onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => toggle.mutate({ id: row.id, status: row.status === 'ENABLED' ? 'DISABLED' : 'ENABLED' })}
          >
            {row.status === 'ENABLED' ? '停用' : '启用'}
          </Button>
        </Space>
      )
    }
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="按车牌/编号搜索"
          style={{ width: 240 }}
          onSearch={(value) => setKeyword(value.trim())}
          enterButton
        />
        <StatusFilter value={status} onChange={setStatus} />
        <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => void list.refetch()}>
          刷新
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          新建车辆
        </Button>
      </Space>

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="车辆列表加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<VehicleRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无车辆' }}
        onRow={(row) => ({ onDoubleClick: () => openEdit(row) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Modal
        title={editing ? `维护车辆：${editing.vehicleNo}` : '新建车辆'}
        open={open}
        width={640}
        destroyOnHidden
        confirmLoading={save.isPending}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={async () => {
          const values = await form.validateFields();
          save.mutate({ id: editing?.id, values });
        }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="vehicleNo"
                label="车牌 / 车辆编号"
                rules={[{ required: true, message: '请填写车牌号' }, { max: 32, message: '不超过 32 字' }]}
              >
                <Input maxLength={32} placeholder="如：浙A12345 或 CART-01" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="vehicleType" label="车辆类型">
                <Select options={Object.entries(VEHICLE_TYPE_META).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="ownership" label="归属">
                <Select options={Object.entries(OWNERSHIP_META).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="loadCapacity" label="额定载重（吨）">
                <InputNumber min={0} precision={3} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="tempZone" label="温区">
                <Input maxLength={32} placeholder="常温 / 冷藏 / 冷冻" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// ---------------- 司机 ----------------

interface DriverFormValues {
  driverName: string;
  phone: string;
  licenseNo?: string;
  licenseClass?: string;
  licenseExpireAt?: string;
  remark?: string;
}

function DriverPanel() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DriverRow | null>(null);
  const [form] = Form.useForm<DriverFormValues>();

  const list = useInfiniteQuery({
    queryKey: ['ops', 'delivery-resource', 'driver', keyword, status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.deliveryResource.listDrivers({
        lastId: pageParam,
        size: PAGE_SIZE,
        keyword: keyword || undefined,
        status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const save = useMutation({
    mutationFn: (payload: { id?: string; values: DriverFormValues }) =>
      hq.deliveryResource.saveDriver({
        id: payload.id,
        driverName: payload.values.driverName.trim(),
        phone: payload.values.phone.trim(),
        licenseNo: payload.values.licenseNo?.trim() || null,
        licenseClass: payload.values.licenseClass?.trim() || null,
        licenseExpireAt: payload.values.licenseExpireAt?.trim() || null,
        remark: payload.values.remark?.trim() || null
      }),
    onSuccess: async (result) => {
      message.success(result.created ? `司机已建档，编码 ${result.driverCode ?? '-'}` : '司机已更新');
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'delivery-resource'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const toggle = useMutation({
    mutationFn: (payload: { id: string; status: 'ENABLED' | 'DISABLED' }) =>
      hq.deliveryResource.setDriverStatus(payload.id, payload.status),
    onSuccess: async () => {
      message.success('状态已更新');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'delivery-resource'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '状态更新失败');
    }
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const openEdit = (row: DriverRow) => {
    setEditing(row);
    form.setFieldsValue({
      driverName: row.driverName,
      phone: row.phone,
      licenseNo: row.licenseNo ?? undefined,
      licenseClass: row.licenseClass ?? undefined,
      licenseExpireAt: row.licenseExpireAt?.slice(0, 10) ?? undefined,
      remark: row.remark ?? undefined
    });
    setOpen(true);
  };

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<DriverRow> = [
    { title: '编码', dataIndex: 'driverCode', width: 100, render: (value: string) => <Typography.Text code>{value}</Typography.Text> },
    { title: '姓名', dataIndex: 'driverName', width: 120 },
    { title: '联系电话', dataIndex: 'phone', width: 140 },
    {
      title: '驾驶证',
      key: 'license',
      width: 220,
      render: (_value: unknown, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{[row.licenseNo, row.licenseClass].filter(Boolean).join(' / ') || '-'}</Typography.Text>
          {row.licenseExpireAt ? (
            <Typography.Text type="secondary">有效期至 {row.licenseExpireAt.slice(0, 10)}</Typography.Text>
          ) : null}
        </Space>
      )
    },
    { title: '状态', dataIndex: 'status', width: 90, render: (value: string) => <StatusTag status={value} /> },
    { title: '备注', dataIndex: 'remark', ellipsis: { showTitle: false }, render: (value: string | null) => <TipText text={value || '-'} /> },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Space size={0}>
          <Button type="link" onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => toggle.mutate({ id: row.id, status: row.status === 'ENABLED' ? 'DISABLED' : 'ENABLED' })}
          >
            {row.status === 'ENABLED' ? '停用' : '启用'}
          </Button>
        </Space>
      )
    }
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="按姓名 / 手机号 / 编码搜索"
          style={{ width: 260 }}
          onSearch={(value) => setKeyword(value.trim())}
          enterButton
        />
        <StatusFilter value={status} onChange={setStatus} />
        <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => void list.refetch()}>
          刷新
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          新建司机
        </Button>
      </Space>

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="司机列表加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<DriverRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无司机' }}
        onRow={(row) => ({ onDoubleClick: () => openEdit(row) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Modal
        title={editing ? `维护司机：${editing.driverCode}` : '新建司机'}
        open={open}
        width={720}
        destroyOnHidden
        confirmLoading={save.isPending}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={async () => {
          const values = await form.validateFields();
          save.mutate({ id: editing?.id, values });
        }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="driverName"
                label="姓名"
                rules={[{ required: true, message: '请填写司机姓名' }, { max: 64, message: '不超过 64 字' }]}
              >
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="联系电话"
                rules={[{ required: true, message: '请填写联系电话' }, { max: 32, message: '不超过 32 字' }]}
              >
                <Input maxLength={32} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="licenseNo" label="驾驶证号">
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="licenseClass" label="准驾车型">
                <Select
                  allowClear
                  options={['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((value) => ({ value, label: value }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="licenseExpireAt" label="驾驶证有效期" extra="格式 YYYY-MM-DD">
                <Input maxLength={10} placeholder="2028-12-31" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// ---------------- 线路 ----------------

interface RouteFormValues {
  routeName: string;
  fromWarehouseId?: string;
  carrierType?: string;
  driverId?: string;
  vehicleId?: string;
  nodesJson?: string;
  expectedMinutes?: number;
  remark?: string;
}

function RoutePanel() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DeliveryRouteRow | null>(null);
  const [form] = Form.useForm<RouteFormValues>();

  const list = useInfiniteQuery({
    queryKey: ['ops', 'delivery-resource', 'route', keyword, status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.deliveryResource.listRoutes({
        lastId: pageParam,
        size: PAGE_SIZE,
        keyword: keyword || undefined,
        status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  // 线路表单的司机/车辆下拉：仅需启用中的资源，取首屏即可
  const drivers = useQuery({
    queryKey: ['ops', 'delivery-resource', 'driver-options'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.deliveryResource.listDrivers({ size: 200, status: 'ENABLED' }),
    staleTime: 5 * 60 * 1000
  });

  const vehicles = useQuery({
    queryKey: ['ops', 'delivery-resource', 'vehicle-options'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.deliveryResource.listVehicles({ size: 200, status: 'ENABLED' }),
    staleTime: 5 * 60 * 1000
  });

  const driverOptions = useMemo(() => drivers.data?.items ?? [], [drivers.data]);
  const vehicleOptions = useMemo(() => vehicles.data?.items ?? [], [vehicles.data]);

  const save = useMutation({
    mutationFn: (payload: { id?: string; values: RouteFormValues }) =>
      hq.deliveryResource.saveRoute({
        id: payload.id,
        routeName: payload.values.routeName.trim(),
        fromWarehouseId: payload.values.fromWarehouseId ?? '0',
        carrierType: payload.values.carrierType ?? 'SELF',
        driverId: payload.values.driverId ?? '0',
        vehicleId: payload.values.vehicleId ?? '0',
        nodesJson: payload.values.nodesJson?.trim() || null,
        expectedMinutes: payload.values.expectedMinutes ?? 0,
        remark: payload.values.remark?.trim() || null
      }),
    onSuccess: async (result) => {
      message.success(result.created ? `线路已建档，编码 ${result.routeCode ?? '-'}` : '线路已更新');
      setOpen(false);
      setEditing(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'delivery-resource'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败，请稍后重试');
    }
  });

  const toggle = useMutation({
    mutationFn: (payload: { id: string; status: 'ENABLED' | 'DISABLED' }) =>
      hq.deliveryResource.setRouteStatus(payload.id, payload.status),
    onSuccess: async () => {
      message.success('状态已更新');
      await queryClient.invalidateQueries({ queryKey: ['ops', 'delivery-resource'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '状态更新失败');
    }
  });

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({ carrierType: 'SELF', expectedMinutes: 0 });
    setOpen(true);
  };

  const openEdit = (row: DeliveryRouteRow) => {
    setEditing(row);
    form.setFieldsValue({
      routeName: row.routeName,
      fromWarehouseId: row.fromWarehouseId === '0' ? undefined : row.fromWarehouseId,
      carrierType: row.carrierType,
      driverId: row.driverId === '0' ? undefined : row.driverId,
      vehicleId: row.vehicleId === '0' ? undefined : row.vehicleId,
      nodesJson: row.nodesJson ?? undefined,
      expectedMinutes: row.expectedMinutes,
      remark: row.remark ?? undefined
    });
    setOpen(true);
  };

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<DeliveryRouteRow> = [
    { title: '编码', dataIndex: 'routeCode', width: 100, render: (value: string) => <Typography.Text code>{value}</Typography.Text> },
    { title: '线路名称', dataIndex: 'routeName', ellipsis: { showTitle: false }, render: (value: string) => <TipText text={value} /> },
    {
      title: '承运',
      dataIndex: 'carrierType',
      width: 110,
      render: (value: string) => CARRIER_META[value] ?? value
    },
    {
      title: '默认司机 / 车辆',
      key: 'resources',
      width: 200,
      render: (_value: unknown, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{row.driverName || '-'}</Typography.Text>
          <Typography.Text type="secondary">{row.vehicleNo || '-'}</Typography.Text>
        </Space>
      )
    },
    { title: '标准时长(分)', dataIndex: 'expectedMinutes', width: 110, align: 'right' },
    { title: '状态', dataIndex: 'status', width: 90, render: (value: string) => <StatusTag status={value} /> },
    { title: '备注', dataIndex: 'remark', ellipsis: { showTitle: false }, render: (value: string | null) => <TipText text={value || '-'} /> },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Space size={0}>
          <Button type="link" onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => toggle.mutate({ id: row.id, status: row.status === 'ENABLED' ? 'DISABLED' : 'ENABLED' })}
          >
            {row.status === 'ENABLED' ? '停用' : '启用'}
          </Button>
        </Space>
      )
    }
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="按线路名称 / 编码搜索"
          style={{ width: 260 }}
          onSearch={(value) => setKeyword(value.trim())}
          enterButton
        />
        <StatusFilter value={status} onChange={setStatus} />
        <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => void list.refetch()}>
          刷新
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          新建线路
        </Button>
      </Space>

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="线路列表加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<DeliveryRouteRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无线路' }}
        onRow={(row) => ({ onDoubleClick: () => openEdit(row) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Modal
        title={editing ? `维护线路：${editing.routeCode}` : '新建线路'}
        open={open}
        width={720}
        destroyOnHidden
        confirmLoading={save.isPending}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={async () => {
          const values = await form.validateFields();
          save.mutate({ id: editing?.id, values });
        }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="routeName"
                label="线路名称"
                rules={[{ required: true, message: '请填写线路名称' }, { max: 128, message: '不超过 128 字' }]}
              >
                <Input maxLength={128} placeholder="如：门诊楼 3F 巡回线" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="carrierType" label="承运方式">
                <Select options={Object.entries(CARRIER_META).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="driverId" label="默认司机">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="选择司机（仅启用中）"
                  options={driverOptions.map((item) => ({
                    value: item.id,
                    label: `${item.driverCode} ${item.driverName}`
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="vehicleId" label="默认车辆">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="选择车辆（仅启用中）"
                  options={vehicleOptions.map((item) => ({ value: item.id, label: item.vehicleNo }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="expectedMinutes" label="标准时长（分钟）" extra="用于时效基线与超时判定">
                <InputNumber min={0} precision={0} style={{ width: '100%' }} addonAfter="分" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="fromWarehouseId" label="起点仓 ID" extra="选填，留空表示随单据确定">
                <Input maxLength={20} placeholder="仓库主键" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="nodesJson" label="途经节点（JSON 数组）" extra="选填；如 [{&quot;seq&quot;:1,&quot;spaceNodeId&quot;:123}]">
            <Input.TextArea rows={3} placeholder="[{&quot;seq&quot;:1,&quot;spaceNodeId&quot;:123}]" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
